import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import StrategyAgent from './StrategyAgent';

const API_BASE = '';
const ENTITY_TYPES = ['Sole Proprietorship', 'LLC', 'S-Corp', 'C-Corp', 'Partnership', 'Non-Profit'];
const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming','District of Columbia'];
const REVENUE_RANGES = ['Under $50K', '$50K-$100K', '$100K-$250K', '$250K-$500K', '$500K-$1M', '$1M-$5M', '$5M-$10M', '$10M+'];

function StepProgress({ steps }) {
  if (!steps?.length) return null;
  const completed = steps.filter(s => s.status === 'completed').length;
  const pct = Math.round((completed / steps.length) * 100);
  return (
    <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>Analysis Progress</span>
        <span style={{ fontWeight: 600, color: '#1565c0' }}>{pct}%</span>
      </div>
      <div style={{ height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: '#1565c0', borderRadius: 4, transition: 'width 0.5s ease' }} />
      </div>
      {steps.map((step, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 'bold', color: 'white', background: step.status === 'completed' ? '#2e7d32' : step.status === 'active' ? '#1565c0' : '#9e9e9e', animation: step.status === 'active' ? 'pulse 1.5s infinite' : 'none' }}>
            {step.status === 'completed' ? '✓' : step.status === 'active' ? '●' : '○'}
          </span>
          <span style={{ fontSize: 14, color: step.status === 'completed' ? '#2e7d32' : step.status === 'active' ? '#1565c0' : '#9e9e9e', fontWeight: step.status === 'active' ? 600 : 400 }}>{step.label}</span>
        </div>
      ))}
    </div>
  );
}

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => { const h = () => setM(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return m;
}

export default function TaxStrategy() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showSidebar, setShowSidebar] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('plan');
  const [mode, setMode] = useState('existing'); // 'existing' or 'new'
  const [form, setForm] = useState({ entityType: 'LLC', goals: '' });
  const [legalStrategyId, setLegalStrategyId] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const chatEndRef = useRef(null);

  // Pre-fill from legal strategy (via ?prefill= query param)
  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const prefill = search.get('prefill');
    if (prefill) {
      const p = new URLSearchParams(prefill);
      const prefillData = {};
      if (p.get('entityType')) prefillData.entityType = p.get('entityType');
      if (p.get('state')) prefillData.state = p.get('state');
      if (p.get('goals')) prefillData.goals = p.get('goals');
      if (p.get('situationDescription')) prefillData.situationDescription = p.get('situationDescription');
      if (p.get('legalStrategyId')) setLegalStrategyId(p.get('legalStrategyId'));
      if (Object.keys(prefillData).length > 0) {
        setForm(prev => ({ ...prev, ...prefillData }));
        if (prefillData.situationDescription) setMode('new');
      }
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchStrategies();
      // Deep link: load strategy from URL
      if (id) {
        (async () => {
          try {
            const r = await fetch(`${API_BASE}/api/tax-strategy/${id}`, { credentials: 'include' });
            const d = await r.json();
            if (d.success) setCurrent(d.strategy);
          } catch {}
        })();
      }
    }
  }, [user, id]);

  const fetchStrategies = async () => {
    try { const r = await fetch(`${API_BASE}/api/tax-strategies`, { credentials: 'include' }); const d = await r.json(); if (d.success) setStrategies(d.strategies); } catch {}
  };

  const fetchConversation = useCallback(async (uuid) => {
    try { const r = await fetch(`${API_BASE}/api/tax-strategy/${uuid}/conversation`, { credentials: 'include' }); const d = await r.json(); if (d.success) setConversation(d.questions || []); } catch {}
  }, []);

  const pollResult = useCallback((uuid) => {
    const poll = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE}/api/tax-strategy/${uuid}`, { credentials: 'include' });
        const d = await r.json();
        if (d.success) { setCurrent(d.strategy); if (d.strategy.status === 'completed' || d.strategy.status === 'failed') { clearInterval(poll); fetchStrategies(); fetchConversation(uuid); } }
      } catch { clearInterval(poll); }
    }, 3000);
  }, [fetchConversation]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/tax-strategy`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, legalStrategyId }) });
      const d = await r.json();
      if (d.success) { setCurrent({ uuid: d.uuid, status: 'processing', steps: [] }); pollResult(d.uuid); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const reset = () => { setCurrent(null); setForm({ entityType: 'LLC', goals: '' }); setActiveTab('plan'); navigate('/tax-strategy'); };
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const results = current?.results || {};
  const plan = results.plan || {};
  const situation = results.situation || {};
  const strats = results.strategies || {};
  const scenarios = results.scenarios || {};

  const TABS = [
    { key: 'plan', label: 'Tax Plan', icon: '📋' },
    { key: 'situation', label: 'Situation', icon: '📊' },
    { key: 'strategies', label: 'Strategies', icon: '🎯' },
    { key: 'scenarios', label: 'Scenarios', icon: '📈' },
    { key: 'regulations', label: 'Regulations', icon: '⚖️' },
  ];

  // Load conversation when viewing a completed strategy
  useEffect(() => { if (current?.uuid && current?.status === 'completed') fetchConversation(current.uuid); }, [current?.uuid, current?.status, fetchConversation]);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Mobile sidebar toggle */}
      {isMobile && (
        <button onClick={() => setShowSidebar(!showSidebar)}
          style={{ position: 'fixed', bottom: 20, left: 20, zIndex: 30, width: 44, height: 44, borderRadius: '50%', border: 'none', background: '#059669', color: 'white', fontSize: 20, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          ☰
        </button>
      )}

      {/* Left Sidebar */}
      <div style={{ width: isMobile ? '85vw' : 300, maxWidth: 320, flexShrink: 0, background: '#fafbfc', borderRight: '1px solid #e2e8f0', display: isMobile && !showSidebar ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden', position: isMobile ? 'fixed' : 'relative', zIndex: isMobile ? 40 : 'auto', height: '100%', left: 0, top: 0, boxShadow: isMobile ? '4px 0 12px rgba(0,0,0,0.1)' : 'none' }}>
        <div style={{ padding: '20px 16px 12px' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 }}>Your Strategies</h2>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
          {strategies.map(s => (
            <div key={s.uuid} onClick={() => { navigate(`/tax-strategy/${s.uuid}`); setCurrent(s); setActiveTab('plan'); if (isMobile) setShowSidebar(false); }}
              style={{ padding: 12, borderRadius: 8, border: `1px solid ${current?.uuid === s.uuid ? '#059669' : '#e2e8f0'}`, background: current?.uuid === s.uuid ? '#ecfdf5' : 'white', cursor: 'pointer', marginBottom: 8, transition: 'all 0.15s' }}>
              <div style={{ fontWeight: 500, fontSize: 13, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title || 'Processing...'}</div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.entity_type && <span>{s.entity_type}</span>}
                {s.state && <span> · {s.state}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 8, fontWeight: 600, background: s.status === 'completed' ? '#ecfdf5' : s.status === 'failed' ? '#fef2f2' : '#fef3c7', color: s.status === 'completed' ? '#059669' : s.status === 'failed' ? '#dc2626' : '#d97706' }}>{s.status}</span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(s.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
          {strategies.length === 0 && <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, marginTop: 20 }}>No strategies yet</p>}
        </div>
        <div style={{ padding: '8px 12px 12px' }}>
          <button onClick={reset} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#059669', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>New Strategy</button>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {isMobile && showSidebar && <div onClick={() => setShowSidebar(false)} style={{ position: 'fixed', inset: 0, zIndex: 35, background: 'rgba(0,0,0,0.3)' }} />}

      {/* Main Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 12px' : '32px 24px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>Tax Strategy</h1>
              <p style={{ color: '#64748b', marginTop: 4, fontSize: 14 }}>Analyze tax regulations to find the best solutions for tax management</p>
            </div>
            {current && (
              <div style={{ display: 'flex', gap: 8 }}>
                {current.status === 'completed' && (
                  <a href={`/api/tax-strategy/${current.uuid}/pdf`} download
                    style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#475569', cursor: 'pointer', fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    📥 PDF
                  </a>
                )}
                {current.status === 'completed' && (
                  <button onClick={async () => {
                    try {
                      await fetch(`/api/tax-strategy/${current.uuid}/revise`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                      setCurrent({ ...current, status: 'processing', steps: [] });
                      pollResult(current.uuid);
                    } catch {}
                  }} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#475569', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>Re-run</button>
                )}
              </div>
            )}
          </div>

      {/* Form */}
      {!current && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 32 }}>
          {/* Mode Toggle */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 24, background: '#f1f5f9', borderRadius: 10, padding: 4 }}>
            <button onClick={() => { setMode('existing'); set('situationDescription', ''); }} style={{ flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none', background: mode === 'existing' ? 'white' : 'transparent', color: mode === 'existing' ? '#059669' : '#64748b', fontWeight: mode === 'existing' ? 600 : 400, cursor: 'pointer', fontSize: 15, boxShadow: mode === 'existing' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.2s' }}>
              Existing Entity
            </button>
            <button onClick={() => setMode('new')} style={{ flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none', background: mode === 'new' ? 'white' : 'transparent', color: mode === 'new' ? '#059669' : '#64748b', fontWeight: mode === 'new' ? 600 : 400, cursor: 'pointer', fontSize: 15, boxShadow: mode === 'new' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.2s' }}>
              New Entity / Situation
            </button>
          </div>

          {mode === 'existing' ? (
            <>
              <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20 }}>Tell Us About Your Business</h2>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Entity Type *</label>
                  <select value={form.entityType} onChange={e => set('entityType', e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, background: 'white' }}>
                    {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>State</label>
                  <select value={form.state || ''} onChange={e => set('state', e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, background: 'white' }}>
                    <option value="">Select state...</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Annual Revenue</label>
                  <select value={form.annualRevenue || ''} onChange={e => set('annualRevenue', e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, background: 'white' }}>
                    <option value="">Select range...</option>
                    {REVENUE_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Industry</label>
                  <input value={form.industry || ''} onChange={e => set('industry', e.target.value)} placeholder="e.g., Technology, Healthcare" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Number of Employees</label>
                  <input value={form.employees || ''} onChange={e => set('employees', e.target.value)} placeholder="e.g., 5, 1-10" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Current Structure</label>
                  <input value={form.currentStructure || ''} onChange={e => set('currentStructure', e.target.value)} placeholder="e.g., Single-member LLC" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Tax Goals & Questions *</label>
                <textarea value={form.goals} onChange={e => set('goals', e.target.value)} placeholder="What are your tax optimization goals? Any specific questions? e.g., 'Should I elect S-Corp status? How can I maximize deductions? What retirement plans offer the best tax benefits?'" style={{ width: '100%', minHeight: 100, padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </>
          ) : (
            <>
              <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>Describe Your Situation</h2>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>Tell us about your new business, venture, or tax situation. We'll analyze the best entity structure, tax strategies, and planning opportunities.</p>
              <textarea value={form.situationDescription || ''} onChange={e => set('situationDescription', e.target.value)}
                placeholder={"Describe your situation in detail. For example:\n\n• I'm starting a SaaS business with a co-founder. We expect $200K revenue in year 1 and $1M by year 3. We're based in Texas but have customers nationwide. We want to understand the best entity structure for tax purposes.\n\n• I'm a freelance developer making $150K/year as a sole proprietor. I'm considering forming an LLC or S-Corp. I want to minimize self-employment tax and maximize retirement contributions.\n\n• I have an existing C-Corp that's profitable. I'm considering an S-Corp election. I also want to set up a holding company structure for multiple businesses."}
                style={{ width: '100%', minHeight: 200, padding: '14px 16px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
              <div style={{ marginTop: 16 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Specific Questions (optional)</label>
                <textarea value={form.goals} onChange={e => set('goals', e.target.value)} placeholder="Any specific tax questions you'd like answered?" style={{ width: '100%', minHeight: 80, padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </>
          )}

          {/* Document Upload */}
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Supporting Documents (optional)</label>
            <div style={{ border: '2px dashed #d1d5db', borderRadius: 8, padding: 16, textAlign: 'center' }}>
              <input type="file" accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp" multiple id="tax-file-input" style={{ display: 'none' }}
                onChange={e => set('files', [...(form.files || []), ...Array.from(e.target.files || [])])} />
              <label htmlFor="tax-file-input" style={{ cursor: 'pointer', color: '#64748b', fontSize: 14 }}>
                Drop files or <span style={{ color: '#059669', fontWeight: 500 }}>browse</span>
                <br /><span style={{ fontSize: 12, color: '#9ca3af' }}>PDF, Word, Excel, PowerPoint, TXT</span>
              </label>
            </div>
            {form.files?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {form.files.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', borderRadius: 6, padding: '6px 12px', marginBottom: 4, fontSize: 13 }}>
                    <span>📎 {f.name}</span>
                    <button onClick={() => set('files', form.files.filter((_, j) => j !== i))} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={handleSubmit} disabled={!(mode === 'existing' ? form.goals?.trim() : (form.situationDescription?.trim() || form.goals?.trim())) || loading} style={{ marginTop: 20, padding: '12px 28px', borderRadius: 8, border: 'none', background: '#059669', color: 'white', fontSize: 16, fontWeight: 600, cursor: 'pointer', opacity: !(mode === 'existing' ? form.goals?.trim() : (form.situationDescription?.trim() || form.goals?.trim())) ? 0.5 : 1 }}>
            {loading ? 'Starting...' : 'Analyze Tax Strategy'}
          </button>
        </div>
      )}

      {/* Processing */}
      {current?.status === 'processing' && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 32 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Analyzing Tax Strategy...</h2>
          <StepProgress steps={current.steps} />
        </div>
      )}

      {/* Results */}
      {current?.status === 'completed' && results && (
        <>
          {/* Section Navigation */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {TABS.map(t => (
                <button key={t.key} onClick={() => setActiveTab(t.key)}
                  style={{
                    padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: activeTab === t.key ? 600 : 400,
                    border: `1.5px solid ${activeTab === t.key ? '#059669' : '#e2e8f0'}`,
                    background: activeTab === t.key ? '#ecfdf5' : 'white',
                    color: activeTab === t.key ? '#065f46' : '#64748b',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tax Plan Tab */}
          {activeTab === 'plan' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {plan.executiveSummary && (
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Executive Summary</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: '#374151' }}>{plan.executiveSummary}</p>
                  {plan.totalEstimatedSavings && (
                    <div style={{ marginTop: 12, padding: 14, borderRadius: 8, background: '#ecfdf5', border: '1px solid #a7f3d0', textAlign: 'center' }}>
                      <span style={{ fontSize: 14, color: '#065f46' }}>Estimated Annual Savings: </span>
                      <span style={{ fontSize: 24, fontWeight: 700, color: '#059669' }}>{plan.totalEstimatedSavings}</span>
                    </div>
                  )}
                </div>
              )}
              {plan.immediateActions?.length > 0 && (
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Immediate Actions</h3>
                  {plan.immediateActions.map((a, i) => (
                    <div key={i} style={{ padding: 12, borderRadius: 8, background: '#f8fafc', borderLeft: '4px solid #059669', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>{a.action}</div>
                        {a.deadline && <div style={{ fontSize: 12, color: '#d97706', marginTop: 2 }}>Deadline: {a.deadline}</div>}
                      </div>
                      {a.estimatedSavings && <span style={{ fontSize: 13, fontWeight: 600, color: '#059669', whiteSpace: 'nowrap' }}>{a.estimatedSavings}</span>}
                    </div>
                  ))}
                </div>
              )}
              {plan.quarterlyActions?.length > 0 && (
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Quarterly Calendar</h3>
                  {plan.quarterlyActions.map((q, i) => (
                    <div key={i} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#6366f1', marginBottom: 4 }}>{q.quarter}</div>
                      {q.actions?.map((a, j) => <div key={j} style={{ fontSize: 13, padding: '4px 10px', borderRadius: 4, background: '#f8fafc', marginBottom: 4 }}>✓ {a}</div>)}
                    </div>
                  ))}
                </div>
              )}
              {plan.warnings?.length > 0 && (
                <div style={{ background: '#fef2f2', borderRadius: 12, border: '1px solid #fecaca', padding: 20 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#991b1b', marginBottom: 8 }}>Warnings</h4>
                  {plan.warnings.map((w, i) => <div key={i} style={{ fontSize: 13, color: '#991b1b', marginBottom: 4 }}>⚠ {w}</div>)}
                </div>
              )}
            </div>
          )}

          {/* Situation Tab */}
          {activeTab === 'situation' && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Current Tax Situation</h3>
              {situation.currentTaxBurden && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
                  {Object.entries(situation.currentTaxBurden).map(([k, v]) => (
                    <div key={k} style={{ padding: 14, borderRadius: 8, background: '#f8fafc', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{k.replace(/([A-Z])/g, ' $1').trim()}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>{v}</div>
                    </div>
                  ))}
                </div>
              )}
              {situation.keyTaxConcerns?.length > 0 && (
                <div>
                  <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Key Concerns</h4>
                  {situation.keyTaxConcerns.map((c, i) => (
                    <div key={i} style={{ padding: 10, borderRadius: 8, borderLeft: `4px solid ${c.severity === 'high' ? '#dc2626' : c.severity === 'medium' ? '#d97706' : '#059669'}`, background: '#f8fafc', marginBottom: 8 }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{c.concern}</div>
                      <div style={{ fontSize: 13, color: '#64748b' }}>{c.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Strategies Tab */}
          {activeTab === 'strategies' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {strats.entityRestructuring?.recommended && (
                <div style={{ background: '#eff6ff', borderRadius: 12, border: '2px solid #3b82f6', padding: 20 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1e40af', marginBottom: 8 }}>Entity Restructuring Recommended</h3>
                  <p style={{ fontSize: 14 }}>{strats.entityRestructuring.currentEntity} → <strong>{strats.entityRestructuring.proposedEntity}</strong></p>
                  <p style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>{strats.entityRestructuring.reason}</p>
                  {strats.entityRestructuring.estimatedSavings && <div style={{ fontSize: 15, fontWeight: 600, color: '#059669', marginTop: 8 }}>Estimated savings: {strats.entityRestructuring.estimatedSavings}</div>}
                </div>
              )}
              {strats.strategies?.map((s, i) => (
                <div key={i} style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: s.category === 'entity_structure' ? '#dbeafe' : s.category === 'deductions' ? '#dcfce7' : s.category === 'credits' ? '#fef3c7' : '#f3e8ff', color: s.category === 'entity_structure' ? '#1e40af' : s.category === 'deductions' ? '#166534' : s.category === 'credits' ? '#92400e' : '#6b21a8', fontWeight: 600, marginRight: 8 }}>{s.category?.replace(/_/g, ' ')}</span>
                      <span style={{ fontWeight: 600, fontSize: 15 }}>{s.name}</span>
                    </div>
                    {s.estimatedSavings && <span style={{ fontWeight: 600, color: '#059669', fontSize: 14 }}>{s.estimatedSavings}</span>}
                  </div>
                  <p style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>{s.description}</p>
                  <div style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                    <span style={{ padding: '2px 8px', borderRadius: 4, background: s.implementationComplexity === 'simple' ? '#ecfdf5' : s.implementationComplexity === 'moderate' ? '#fef3c7' : '#fef2f2' }}>{s.implementationComplexity}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 4, background: s.risk === 'low' ? '#ecfdf5' : s.risk === 'medium' ? '#fef3c7' : '#fef2f2' }}>Risk: {s.risk}</span>
                    {s.timeline && <span style={{ padding: '2px 8px', borderRadius: 4, background: '#f1f5f9' }}>{s.timeline}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Scenarios Tab */}
          {activeTab === 'scenarios' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {scenarios.comparisonTable?.length > 0 && (
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, overflowX: 'auto' }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Comparison</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead><tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ textAlign: 'left', padding: 8 }}>Item</th>
                      <th style={{ textAlign: 'right', padding: 8 }}>Current</th>
                      <th style={{ textAlign: 'right', padding: 8, color: '#059669' }}>Optimized</th>
                      {scenarios.comparisonTable[0]?.aggressive && <th style={{ textAlign: 'right', padding: 8, color: '#d97706' }}>Aggressive</th>}
                    </tr></thead>
                    <tbody>{scenarios.comparisonTable.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: 8, fontWeight: 500 }}>{row.item}</td>
                        <td style={{ padding: 8, textAlign: 'right' }}>{row.current}</td>
                        <td style={{ padding: 8, textAlign: 'right', color: '#059669', fontWeight: 500 }}>{row.optimized}</td>
                        {row.aggressive && <td style={{ padding: 8, textAlign: 'right', color: '#d97706' }}>{row.aggressive}</td>}
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
              {scenarios.fiveYearProjection?.length > 0 && (
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>5-Year Projection</h3>
                  {scenarios.fiveYearProjection.map((y, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 10, borderRadius: 8, background: i % 2 === 0 ? '#f8fafc' : 'white' }}>
                      <span style={{ width: 60, fontWeight: 600, color: '#6366f1' }}>Year {y.year}</span>
                      <span style={{ flex: 1, fontSize: 13 }}>Current: {y.currentPath}</span>
                      <span style={{ flex: 1, fontSize: 13, color: '#059669' }}>Optimized: {y.optimizedPath}</span>
                      <span style={{ fontWeight: 600, color: '#059669', fontSize: 13 }}>{y.cumulativeSavings}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Regulations Tab */}
          {activeTab === 'regulations' && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Applicable Tax Regulations</h3>
              {results.regulations?.federalTaxRules?.map((r, i) => (
                <div key={i} style={{ padding: 10, borderRadius: 8, background: '#f8fafc', marginBottom: 8, borderLeft: '4px solid #6366f1' }}>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{r.rule} <span style={{ fontSize: 12, color: '#6366f1' }}>{r.section}</span></div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>{r.opportunity}</div>
                </div>
              ))}
              {results.regulations?.recentChanges?.length > 0 && (
                <>
                  <h4 style={{ fontSize: 15, fontWeight: 600, marginTop: 16, marginBottom: 8 }}>Recent Changes</h4>
                  {results.regulations.recentChanges.map((c, i) => (
                    <div key={i} style={{ padding: 10, borderRadius: 8, background: '#fef3c7', marginBottom: 8, borderLeft: '4px solid #d97706' }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{c.change}</div>
                      <div style={{ fontSize: 12, color: '#92400e' }}>Effective: {c.effectiveDate} | Impact: {c.impact}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Conversation Tab — removed, replaced by StrategyAgent panel */}
          {false && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 500 }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Tax Strategy Conversation</h3>
                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>Discuss, refine, or upload documents for deeper analysis.</p>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
                {conversation.length === 0 && !chatLoading && <p style={{ textAlign: 'center', color: '#999', fontSize: 13, marginTop: 40 }}>Start a conversation to refine your tax strategy.</p>}
                {conversation.map((q, i) => (
                  <div key={q.id || i} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                      <div style={{ maxWidth: '85%', padding: '10px 14px', background: q.question.startsWith('[Uploaded:') ? '#ecfdf5' : '#059669', color: q.question.startsWith('[Uploaded:') ? '#065f46' : 'white', borderRadius: '14px 14px 2px 14px', fontSize: 13 }}>
                        {q.question.startsWith('[Uploaded:') ? '📎 ' : ''}{q.question}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{ maxWidth: '85%', padding: '10px 14px', background: '#f3f4f6', borderRadius: '14px 14px 14px 2px', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{q.answer}</div>
                    </div>
                  </div>
                ))}
                {chatLoading && <div style={{ display: 'flex', justifyContent: 'flex-start' }}><div style={{ padding: '10px 14px', background: '#f3f4f6', borderRadius: 14, fontSize: 13, color: '#999' }}>Thinking...</div></div>}
                <div ref={chatEndRef} />
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ cursor: 'pointer', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', display: 'flex', color: uploading ? '#9ca3af' : '#059669' }}>
                  <input type="file" accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp" style={{ display: 'none' }} disabled={uploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      setUploading(true);
                      try {
                        const fd = new FormData(); fd.append('file', file);
                        const r = await fetch(`${API_BASE}/api/tax-strategy/${current.uuid}/upload`, { method: 'POST', credentials: 'include', body: fd });
                        const d = await r.json();
                        if (d.success) setConversation(prev => [...prev, d.question]);
                      } catch (err) { console.error(err); }
                      setUploading(false); e.target.value = '';
                    }} />
                  {uploading ? '...' : '📎'}
                </label>
                <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key !== 'Enter' || !chatInput.trim() || chatLoading) return;
                    setChatLoading(true);
                    try {
                      const r = await fetch(`${API_BASE}/api/tax-strategy/${current.uuid}/conversation`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: chatInput.trim() }) });
                      const d = await r.json();
                      if (d.success) { setConversation(prev => [...prev, d.question]); setChatInput(''); }
                    } catch {}
                    setChatLoading(false);
                    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                  }}
                  placeholder="Ask about your tax strategy or provide new info..."
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none' }} />
                <button disabled={!chatInput.trim() || chatLoading}
                  onClick={async () => {
                    if (!chatInput.trim() || chatLoading) return;
                    setChatLoading(true);
                    try {
                      const r = await fetch(`${API_BASE}/api/tax-strategy/${current.uuid}/conversation`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: chatInput.trim() }) });
                      const d = await r.json();
                      if (d.success) { setConversation(prev => [...prev, d.question]); setChatInput(''); }
                    } catch {}
                    setChatLoading(false);
                    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                  }}
                  style={{ padding: '10px 18px', background: (!chatInput.trim() || chatLoading) ? '#d1d5db' : '#059669', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Send</button>
              </div>
              {conversation.length > 0 && (
                <div style={{ padding: '8px 20px', borderTop: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', justifyContent: 'center' }}>
                  <button onClick={async () => {
                    if (!window.confirm('Revise strategy with all conversation feedback?')) return;
                    try { await fetch(`${API_BASE}/api/tax-strategy/${current.uuid}/revise`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }); setCurrent({ ...current, status: 'processing', steps: [] }); pollResult(current.uuid); } catch {}
                  }} style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #059669', background: 'white', color: '#059669', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Revise Strategy with Conversation Feedback
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {current?.status === 'failed' && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 20 }}>
          <p style={{ color: '#dc2626', fontWeight: 500 }}>Analysis failed. <button onClick={reset} style={{ color: '#dc2626', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}>Try again</button></p>
        </div>
      )}

      <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, marginTop: 32 }}>AI-assisted tax analysis. Consult a CPA or tax attorney before implementing tax strategies.</p>
        </div>
      </div>

      {/* Agent Panel — inside the flex layout */}
      {current?.uuid && (current?.status === 'completed' || current?.status === 'processing') && (
        <StrategyAgent
          strategyUuid={current.uuid}
          strategyType="tax-strategy"
          onRevisionStarted={() => { pollResult(current.uuid); }}
          onRevisionComplete={() => {
            (async () => {
              try {
                const r = await fetch(`/api/tax-strategy/${current.uuid}`, { credentials: 'include' });
                const d = await r.json();
                if (d.success) setCurrent(d.strategy);
                fetchStrategies();
              } catch {}
            })();
          }}
        />
      )}
    </div>
  );
}
